import React, { useEffect, useState } from "react";
import { Globe, Loader2 } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

// One-time nudge for existing accounts with no country set.
export function CountryPrompt() {
  const { user, setUser } = useAuth();
  const [countries, setCountries] = useState([]);
  const [country, setCountry] = useState("");
  const [saving, setSaving] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const needs = user && user.account_type && !user.country && !dismissed;

  useEffect(() => {
    if (needs) {
      api.get("/public/countries").then((r) => setCountries(r.data.countries || [])).catch(() => {});
    }
  }, [needs]);

  if (!needs) return null;

  const save = async () => {
    if (!country) return toast.error("Please pick your country");
    setSaving(true);
    try {
      const { data } = await api.put("/profile", { country });
      setUser(data);
      toast.success("Country saved");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-testid="country-prompt">
      <div className="absolute inset-0 bg-slate-950/60 backdrop-blur-sm" />
      <div className="relative w-full max-w-md bg-card border border-border rounded-2xl shadow-2xl p-6">
        <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
          <Globe className="w-6 h-6 text-primary" />
        </div>
        <h2 className="font-display text-xl font-bold text-slate-900 dark:text-slate-100">
          Where are you based?
        </h2>
        <p className="text-sm text-muted-foreground mt-1.5">
          {user.account_type === "contractor"
            ? "We'll show you vendors in your country first. You can change this any time in your profile."
            : "This helps contractors in your country find you first. You can change it later in your profile."}
        </p>

        <div className="mt-5">
          <Label>Country</Label>
          <select
            data-testid="country-prompt-select"
            value={country}
            onChange={(e) => setCountry(e.target.value)}
            className="mt-1.5 flex h-11 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            <option value="">Select your country</option>
            {countries.map((cn) => <option key={cn} value={cn}>{cn}</option>)}
          </select>
        </div>

        <div className="mt-6 flex items-center justify-between gap-3">
          <button
            data-testid="country-prompt-later"
            onClick={() => setDismissed(true)}
            className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
          >
            Maybe later
          </button>
          <Button data-testid="country-prompt-save" onClick={save} disabled={saving} className="gap-2 h-11 font-semibold px-6">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            Save
          </Button>
        </div>
      </div>
    </div>
  );
}
